import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { Currency, Hostel } from '@app/services/api/api.service';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { LocaleService } from '@app/locale.service';

@Component({
  selector: 'app-hostel-details',
  templateUrl: './hostel-details.component.html',
  styleUrls: ['./hostel-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ transform: 'translateY(-8px)', opacity: 0.4 }),
        animate(
          '0.15s ease-in',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
    ]),
  ],
})
export class HostelDetailsComponent {
  @Input() public hostel?: Hostel;
  @Input() public googleReviewData?: { rating: number; reviews_count: number }; // Add this input to get google reviews data

  @Output() public anchorClicked = new EventEmitter<string>();

  public currency: ReplaySubject<Currency>;
  private destroyed = new Subject<void>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private localeService: LocaleService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  ngOnInit() {
    this.currency = this.localeService.currentCurrency;
}

  get isServer() {
    return isPlatformServer(this.platformId);
  }

  public goToAnchor(id: string) {
    this.anchorClicked.emit(id);
  }

  public copyCurrentURL() {
    navigator.clipboard.writeText(this.document.location.href).catch((err) => {
      console.error('Failed to copy URL: ', err);
    });
  }

  public openInGoogleMaps() {
    if (this.hostel && this.hostel.address) {
      const address = `${this.hostel.address.geoAddress}`;
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`;
      window.open(url, '_blank');
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  // Computed property for combined rating
  get combinedRating(): number | null {
    const hostelRating = this.hostel.overallRating || 0;
    const googleRating = this.hostel.google_review_data
      ? this.hostel.google_review_data.rating || 0
      : 0;

    if (!hostelRating || !googleRating) return hostelRating + googleRating;

    return (hostelRating + googleRating) / 2;
  }

  // Computed property for combined review count
  get combinedReviewCount(): number {
    const hostelReviews = this.hostel?.totalReviews || 0;
    const googleReviews = this.hostel?.google_review_data?.reviews_count || 0;
    return hostelReviews + googleReviews;
  }
}
